import {
  BaselineSeries,
  LineSeries,
  createSeriesMarkers,
} from "lightweight-charts";

const overlayStateByChart = new WeakMap();

function getOverlayState(chartApi) {
  if (!chartApi?.chart) {
    return null;
  }

  let state = overlayStateByChart.get(chartApi.chart);

  if (!state) {
    state = {
      markerLayer: null,
      bosLineSeries: [],
      zoneFillSeries: [],
      zoneBorderSeries: [],
      zoneLabelLayer: null,
      zonePriceLines: [],
      bosHighlightLayer: null,
      trendLineSeries: null,
      trendLineGlowSeries: null,
    };
    overlayStateByChart.set(chartApi.chart, state);
  }

  return state;
}

function ensureMarkerLayer(chartApi) {
  const state = getOverlayState(chartApi);

  if (!state || !chartApi?.candleSeries) {
    return null;
  }

  if (!state.markerLayer) {
    state.markerLayer = createSeriesMarkers(chartApi.candleSeries, []);
  }

  return state.markerLayer;
}

function buildBosMarkers(bosEvents = []) {
  return bosEvents.map((event) => ({
    id: `bos-${event.type}-${event.bosCandleTime}`,
    time: event.bosCandleTime,
    position: event.type === "UP" ? "belowBar" : "aboveBar",
    shape: event.type === "UP" ? "arrowUp" : "arrowDown",
    color: event.type === "UP" ? "#22c55e" : "#ef4444",
    text: "",
  }));
}

function buildActiveBosSwingMarker(structure, candles = []) {
  if (
    !structure?.event ||
    structure.swingIndex == null ||
    structure.bosPrice == null
  ) {
    return [];
  }

  const swingCandle = candles[structure.swingIndex];
  const isBullish = structure.direction === "BULLISH";

  return [
    {
      id: `bos-swing-${structure.swingIndex}-${structure.direction}`,
      time: swingCandle?.time ?? structure.bosCandleTime,
      position: isBullish ? "aboveBar" : "belowBar",
      shape: "circle",
      color: isBullish ? "#f59e0b" : "#38bdf8",
      text: "",
    },
  ];
}

function uniqueMarkers(markers = []) {
  const seen = new Set();

  return markers.filter((marker) => {
    if (!marker?.id || seen.has(marker.id)) {
      return false;
    }

    seen.add(marker.id);
    return true;
  });
}

function getZonePalette(zone) {
  const palettesByZone = {
    hidden: {
      fresh: {
        border: "#8b5cf6",
        fillTop: "rgba(139, 92, 246, 0.20)",
        fillBottom: "rgba(139, 92, 246, 0.06)",
        label: "#c4b5fd",
      },
      tested: {
        border: "#c084fc",
        fillTop: "rgba(192, 132, 252, 0.24)",
        fillBottom: "rgba(192, 132, 252, 0.10)",
        label: "#e9d5ff",
      },
    },
    demand: {
      fresh: {
        border: "#22c55e",
        fillTop: "rgba(34, 197, 94, 0.20)",
        fillBottom: "rgba(34, 197, 94, 0.06)",
        label: "#86efac",
      },
      tested: {
        border: "#38bdf8",
        fillTop: "rgba(56, 189, 248, 0.14)",
        fillBottom: "rgba(14, 165, 233, 0.05)",
        label: "#7dd3fc",
      },
      failed: {
        border: "#94a3b8",
        fillTop: "rgba(148, 163, 184, 0.10)",
        fillBottom: "rgba(100, 116, 139, 0.04)",
        label: "#cbd5e1",
      },
    },
    supply: {
      fresh: {
        border: "#ef4444",
        fillTop: "rgba(239, 68, 68, 0.20)",
        fillBottom: "rgba(239, 68, 68, 0.06)",
        label: "#fca5a5",
      },
      tested: {
        border: "#fb923c",
        fillTop: "rgba(251, 146, 60, 0.14)",
        fillBottom: "rgba(249, 115, 22, 0.05)",
        label: "#fdba74",
      },
      failed: {
        border: "#94a3b8",
        fillTop: "rgba(148, 163, 184, 0.10)",
        fillBottom: "rgba(100, 116, 139, 0.04)",
        label: "#cbd5e1",
      },
    },
    demand_ob: {
      fresh: {
        border: "#14b8a6",
        fillTop: "rgba(20, 184, 166, 0.22)",
        fillBottom: "rgba(13, 148, 136, 0.08)",
        label: "#99f6e4",
      },
      mitigated: {
        border: "rgba(15, 118, 110, 0.55)",
        fillTop: "rgba(15, 118, 110, 0.12)",
        fillBottom: "rgba(15, 118, 110, 0.04)",
        label: "#99f6e4",
      },
    },
    supply_ob: {
      fresh: {
        border: "#f59e0b",
        fillTop: "rgba(245, 158, 11, 0.22)",
        fillBottom: "rgba(217, 119, 6, 0.08)",
        label: "#fde68a",
      },
      mitigated: {
        border: "rgba(180, 83, 9, 0.60)",
        fillTop: "rgba(180, 83, 9, 0.12)",
        fillBottom: "rgba(180, 83, 9, 0.04)",
        label: "#fde68a",
      },
    },
  };

  const zoneKey = zone.zoneName || (zone.isDemand ? "demand" : "supply");
  const stateKey =
    zone.failed || zone.state === "failed"
      ? "failed"
      : zone.state === "MITIGATED"
      ? "mitigated"
      : zone.state === "FRESH"
      ? "fresh"
      : zone.state === "tested" || zone.tested
      ? "tested"
      : "fresh";

  return (
    palettesByZone[zoneKey]?.[stateKey] ||
    palettesByZone[zoneKey]?.fresh ||
    palettesByZone.demand.fresh
  );
}

function buildZoneMarkers(zones = []) {
  return zones.map((zone) => {
    const palette = getZonePalette(zone);

    return {
      id: `${zone.id}-event`,
      time: zone.breakoutTime,
      position: zone.isDemand ? "belowBar" : "aboveBar",
      shape: zone.isDemand ? "arrowUp" : "arrowDown",
      color: palette.label,
      size: 1.4,
      text: "",
    };
  });
}

function getZoneEndTime(zone, timeframe, lastCandleTime = null) {
  const safeTimeframe = Number(timeframe) > 0 ? Number(timeframe) : 300;

  if ((zone.failed || zone.tested || zone.state === "MITIGATED") && zone.testTime) {
    if (typeof lastCandleTime === "number") {
      return Math.min(zone.testTime, lastCandleTime);
    }

    return zone.testTime;
  }

  return Math.max(zone.breakoutTime + safeTimeframe * 150, zone.testTime ?? 0);
}

function ensureZoneLabelLayer(chartApi) {
  const state = getOverlayState(chartApi);
  const container = chartApi?.container;

  if (!state || !container) {
    return null;
  }

  if (!state.zoneLabelLayer || state.zoneLabelLayer.parentElement !== container) {
    state.zoneLabelLayer?.remove();
    state.zoneLabelLayer = document.createElement("div");
    state.zoneLabelLayer.className = "chart-zone-label-layer";
    Object.assign(state.zoneLabelLayer.style, {
      position: "absolute",
      inset: "0",
      pointerEvents: "none",
      zIndex: "4",
      overflow: "hidden",
    });
    container.appendChild(state.zoneLabelLayer);
  }

  return state.zoneLabelLayer;
}

function ensureBosHighlightLayer(chartApi) {
  const state = getOverlayState(chartApi);
  const container = chartApi?.container;

  if (!state || !container) {
    return null;
  }

  if (!state.bosHighlightLayer || state.bosHighlightLayer.parentElement !== container) {
    state.bosHighlightLayer?.remove();
    state.bosHighlightLayer = document.createElement("div");
    state.bosHighlightLayer.className = "chart-bos-highlight-layer";
    Object.assign(state.bosHighlightLayer.style, {
      position: "absolute",
      inset: "0",
      pointerEvents: "none",
      zIndex: "5",
      overflow: "hidden",
    });
    container.appendChild(state.bosHighlightLayer);
  }

  return state.bosHighlightLayer;
}

function clearZoneLabels(chartApi, { removeLayer = false } = {}) {
  const state = getOverlayState(chartApi);

  if (state?.zoneLabelLayer) {
    state.zoneLabelLayer.replaceChildren();

    if (removeLayer) {
      state.zoneLabelLayer.remove();
      state.zoneLabelLayer = null;
    }
  }
}

function clearBosHighlights(chartApi, { removeLayer = false } = {}) {
  const state = getOverlayState(chartApi);

  if (state?.bosHighlightLayer) {
    state.bosHighlightLayer.replaceChildren();

    if (removeLayer) {
      state.bosHighlightLayer.remove();
      state.bosHighlightLayer = null;
    }
  }
}

function createZoneLabelElement(zone, palette, x, y, maxWidth) {
  const label = document.createElement("div");
  label.textContent = zone.label || zone.name || zone.zoneLabel || "Zone";
  Object.assign(label.style, {
    position: "absolute",
    left: `${x}px`,
    top: `${y}px`,
    transform: "translate(-50%, -50%)",
    padding: "2px 8px",
    borderRadius: "10px",
    border: `1px solid ${palette.border}55`,
    background:
      `linear-gradient(180deg, ${palette.fillTop}, rgba(15, 23, 42, 0.72))`,
    color: palette.label,
    fontFamily: "'Segoe UI', 'Trebuchet MS', Arial, sans-serif",
    fontSize: "10px",
    fontWeight: "600",
    letterSpacing: "0.04em",
    textTransform: "capitalize",
    whiteSpace: "nowrap",
    textAlign: "center",
    lineHeight: "1.2",
    boxShadow:
      "0 6px 18px rgba(2, 6, 23, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.04)",
    backdropFilter: "blur(8px)",
    maxWidth: `${Math.max(52, maxWidth)}px`,
    overflow: "hidden",
    textOverflow: "ellipsis",
  });

  if (zone.isHighlighted) {
    ensureZonePulseStyle();
    label.style.animation = "spike-zone-select-pulse 1.4s ease-in-out infinite";
    label.style.setProperty("--zone-pulse-color", palette.border);
  }

  return label;
}

let zonePulseStyleInjected = false;

function ensureZonePulseStyle() {
  if (zonePulseStyleInjected || typeof document === "undefined") {
    return;
  }

  const style = document.createElement("style");
  style.setAttribute("data-spike-zone-pulse", "true");
  style.textContent = `
@keyframes spike-zone-select-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 var(--zone-pulse-color, rgba(56, 189, 248, 0.6)), 0 6px 18px rgba(2, 6, 23, 0.18);
  }
  50% {
    box-shadow: 0 0 0 6px transparent, 0 0 16px 4px var(--zone-pulse-color, rgba(56, 189, 248, 0.6));
  }
}
`;
  document.head.appendChild(style);
  zonePulseStyleInjected = true;
}

function createBosMarkerElement(x, y, color, direction) {
  const marker = document.createElement("div");
  const isUp = direction === "UP";
  Object.assign(marker.style, {
    position: "absolute",
    left: `${x}px`,
    top: `${y}px`,
    transform: isUp ? "translateX(-50%)" : "translate(-50%, -100%)",
    display: "flex",
    flexDirection: isUp ? "column" : "column-reverse",
    alignItems: "center",
    gap: "1px",
    whiteSpace: "nowrap",
    pointerEvents: "none",
  });

  const arrow = document.createElement("span");
  arrow.textContent = direction === "UP" ? "\u2191" : "\u2193";
  Object.assign(arrow.style, {
    color,
    fontFamily: "'Segoe UI Symbol', 'Segoe UI', 'Trebuchet MS', Arial, sans-serif",
    fontSize: "22px",
    fontWeight: "800",
    lineHeight: "1",
    textShadow: `0 0 12px ${color}66, 0 2px 8px rgba(2, 6, 23, 0.9)`,
  });

  const label = document.createElement("span");
  label.textContent = "BOS";
  Object.assign(label.style, {
    color,
    fontFamily: "'Segoe UI', 'Trebuchet MS', Arial, sans-serif",
    fontSize: "10px",
    fontWeight: "800",
    letterSpacing: "0.08em",
    lineHeight: "1",
    textShadow: "0 1px 8px rgba(2, 6, 23, 0.9)",
  });

  marker.appendChild(arrow);
  marker.appendChild(label);

  return marker;
}

function clearBosLines(chartApi) {
  const state = getOverlayState(chartApi);

  state?.bosLineSeries.forEach((series) => {
    try {
      chartApi.chart.removeSeries(series);
    } catch (error) {}
  });

  if (state) {
    state.bosLineSeries = [];
  }
}

function clearZoneSeries(chartApi) {
  const state = getOverlayState(chartApi);

  state?.zoneFillSeries.forEach((series) => {
    try {
      chartApi.chart.removeSeries(series);
    } catch (error) {}
  });

  state?.zoneBorderSeries.forEach((series) => {
    try {
      chartApi.chart.removeSeries(series);
    } catch (error) {}
  });

  if (state) {
    state.zoneFillSeries = [];
    state.zoneBorderSeries = [];
  }
}

function clearZonePriceLines(chartApi) {
  const state = getOverlayState(chartApi);

  if (!chartApi?.candleSeries) {
    if (state) {
      state.zonePriceLines = [];
    }
    return;
  }

  state?.zonePriceLines.forEach((priceLine) => {
    try {
      chartApi.candleSeries.removePriceLine(priceLine);
    } catch (error) {}
  });

  if (state) {
    state.zonePriceLines = [];
  }
}

function isFreshZone(zone) {
  return zone?.state === "fresh" || zone?.state === "FRESH";
}

function buildFreshZonePriceLineSpecs(zones = []) {
  const seenPrices = new Set();

  return zones.flatMap((zone) => {
    if (!isFreshZone(zone)) {
      return [];
    }

    const palette = getZonePalette(zone);

    return [Number(zone.high), Number(zone.low)]
      .filter((price) => Number.isFinite(price))
      .filter((price) => {
        const key = price.toFixed(8);

        if (seenPrices.has(key)) {
          return false;
        }

        seenPrices.add(key);
        return true;
      })
      .map((price) => ({
        price,
        color: palette.border,
      }));
  });
}

function syncFreshZonePriceLines(chartApi, zones = []) {
  const state = getOverlayState(chartApi);

  clearZonePriceLines(chartApi);

  if (!chartApi?.candleSeries || !state) {
    return;
  }

  buildFreshZonePriceLineSpecs(zones).forEach((spec) => {
    const priceLine = chartApi.candleSeries.createPriceLine({
      price: spec.price,
      color: spec.color,
      lineWidth: 1,
      lineStyle: 2,
      lineVisible: false,
      axisLabelVisible: true,
      title: "",
    });

    state.zonePriceLines.push(priceLine);
  });
}

function drawBosLines(chartApi, bosEvents = []) {
  const state = getOverlayState(chartApi);

  clearBosLines(chartApi);

  bosEvents.forEach((event) => {
    const color = event.type === "UP" ? "#22c55e" : "#ef4444";
    const series = chartApi.chart.addSeries(LineSeries, {
      color,
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    series.setData([
      { time: event.swingTime, value: event.price },
      { time: event.bosCandleTime, value: event.price },
    ]);

    state?.bosLineSeries.push(series);
  });
}

function clearTrendLine(chartApi) {
  const state = getOverlayState(chartApi);

  if (state?.trendLineSeries) {
    try {
      chartApi.chart.removeSeries(state.trendLineSeries);
    } catch (error) {}

    state.trendLineSeries = null;
  }

  if (state?.trendLineGlowSeries) {
    try {
      chartApi.chart.removeSeries(state.trendLineGlowSeries);
    } catch (error) {}

    state.trendLineGlowSeries = null;
  }
}

export function drawTrendLine(chartApi, trendLine, settings = {}, glowIntensity = 0) {
  if (!chartApi?.chart) {
    return;
  }

  const state = getOverlayState(chartApi);

  if (!state) {
    return;
  }

  if (!trendLine) {
    clearTrendLine(chartApi);
    return;
  }

  const color = trendLine.isGlowing
    ? settings.trendLineGlowColor || "#00FFFF"
    : settings.trendLineColor || "#FFFFFF";
  const lineWidth = trendLine.isGlowing
    ? settings.trendLineGlowWidth || 5
    : settings.trendLineWidth || 2;

  if (!state.trendLineSeries) {
    state.trendLineSeries = chartApi.chart.addSeries(LineSeries, {
      color,
      lineWidth,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });
  } else {
    state.trendLineSeries.applyOptions({ color, lineWidth });
  }

  state.trendLineSeries.setData(trendLine.points);

  if (trendLine.isGlowing) {
    if (!state.trendLineGlowSeries) {
      state.trendLineGlowSeries = chartApi.chart.addSeries(LineSeries, {
        color: settings.trendLineGlowColor || "#00FFFF",
        lineWidth: Math.max(lineWidth + 2, 6),
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
      });
    }
    state.trendLineGlowSeries.applyOptions({
      color: `${settings.trendLineGlowColor || "#00FFFF"}${Math.round((0.25 + glowIntensity * 0.45) * 255).toString(16).padStart(2, "0")}`,
      lineWidth: Math.max(lineWidth + Math.round(glowIntensity * 3), 6),
    });
    state.trendLineGlowSeries.setData(trendLine.points);
  } else if (state.trendLineGlowSeries) {
    try {
      chartApi.chart.removeSeries(state.trendLineGlowSeries);
    } catch (error) {}
    state.trendLineGlowSeries = null;
  }
}

export function resetOverlays(chartApi) {
  if (!chartApi?.chart) {
    return;
  }

  clearBosLines(chartApi);
  clearZoneSeries(chartApi);
  clearZonePriceLines(chartApi);
  clearBosHighlights(chartApi, { removeLayer: true });
  clearZoneLabels(chartApi, { removeLayer: true });
  clearTrendLine(chartApi);

  const state = getOverlayState(chartApi);

  if (state?.markerLayer) {
    state.markerLayer.setMarkers([]);
    state.markerLayer = null;
  }
}

function drawZoneBands(chartApi, zones = [], timeframe, lastCandleTime = null) {
  const state = getOverlayState(chartApi);

  clearZoneSeries(chartApi);

  if (!zones.length || !state) {
    return;
  }

  const safeTimeframe = Number(timeframe) > 0 ? Number(timeframe) : 300;

  zones.forEach((zone) => {
    const palette = getZonePalette(zone);
    const endTime = getZoneEndTime(zone, safeTimeframe, lastCandleTime);

    const fillSeries = chartApi.chart.addSeries(BaselineSeries, {
      baseValue: {
        type: "price",
        price: zone.low,
      },
      lineVisible: false,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
      topLineColor: palette.border,
      topFillColor1: palette.fillTop,
      topFillColor2: palette.fillBottom,
      bottomLineColor: "rgba(0, 0, 0, 0)",
      bottomFillColor1: "rgba(0, 0, 0, 0)",
      bottomFillColor2: "rgba(0, 0, 0, 0)",
    });

    fillSeries.setData([
      { time: zone.startTime, value: zone.high },
      { time: endTime, value: zone.high },
    ]);

    const topBorder = chartApi.chart.addSeries(LineSeries, {
      color: palette.border,
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    topBorder.setData([
      { time: zone.startTime, value: zone.high },
      { time: endTime, value: zone.high },
    ]);

    const bottomBorder = chartApi.chart.addSeries(LineSeries, {
      color: palette.border,
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    bottomBorder.setData([
      { time: zone.startTime, value: zone.low },
      { time: endTime, value: zone.low },
    ]);

    state.zoneFillSeries.push(fillSeries);
    state.zoneBorderSeries.push(topBorder, bottomBorder);
  });
}

function syncBosHighlights(chartApi, structure, candles = []) {
  if (!chartApi?.chart || !chartApi?.candleSeries || !chartApi?.container) {
    return;
  }

  const layer = ensureBosHighlightLayer(chartApi);

  if (!layer) {
    return;
  }

  clearBosHighlights(chartApi);

  const chartWidth = chartApi.container.clientWidth;
  const chartHeight = chartApi.container.clientHeight;
  const bosEvents = structure?.bosEvents || [];
  const candleIndexByTime = new Map(candles.map((candle, index) => [candle.time, index]));

  bosEvents.forEach((event) => {
    const candleIndex =
      Number.isInteger(event.bosCandleIndex) && event.bosCandleIndex >= 0
        ? event.bosCandleIndex
        : candleIndexByTime.get(event.bosCandleTime);
    const candle = candleIndex == null ? null : candles[candleIndex];

    if (!candle) {
      return;
    }

    const x = chartApi.chart.timeScale().timeToCoordinate(candle.time);
    const yHigh = chartApi.candleSeries.priceToCoordinate(candle.high);
    const yLow = chartApi.candleSeries.priceToCoordinate(candle.low);

    if (x == null || yHigh == null || yLow == null) {
      return;
    }

    const isBullishBreak = event.direction === "BULLISH" || event.type === "UP";
    const arrowDirection = isBullishBreak ? "UP" : "DOWN";
    const y = isBullishBreak
      ? Math.min(chartHeight - 14, yLow + 2)
      : Math.max(14, yHigh - 2);

    if (x < 10 || x > chartWidth - 10 || y < 10 || y > chartHeight - 10) {
      return;
    }

    layer.appendChild(
      createBosMarkerElement(
        x,
        y,
        event.type === "UP" ? "#22c55e" : "#ef4444",
        arrowDirection
      )
    );
  });
}

export function drawStructureOverlays(chartApi, structure, candles, options = {}) {
  if (!chartApi?.chart || !chartApi?.candleSeries) {
    return;
  }

  const lastCandleTime = candles[candles.length - 1]?.time ?? null;

  clearBosLines(chartApi);
  clearZoneSeries(chartApi);

  const markers = uniqueMarkers([
    ...buildBosMarkers(structure?.bosEvents || []),
    ...buildActiveBosSwingMarker(structure, candles),
    ...buildZoneMarkers(options.zones || []),
  ]);

  ensureMarkerLayer(chartApi)?.setMarkers(markers);

  if (structure?.event) {
    drawBosLines(chartApi, structure.bosEvents);
  }

  drawZoneBands(chartApi, options.zones || [], options.timeframe, lastCandleTime);
  syncFreshZonePriceLines(chartApi, options.zones || []);
  syncOverlayLabels(
    chartApi,
    structure,
    candles,
    options.zones || [],
    options.timeframe,
    lastCandleTime
  );
}

function syncZoneLabels(chartApi, zones = [], timeframe, lastCandleTime = null) {
  if (!chartApi?.chart || !chartApi?.candleSeries || !chartApi?.container) {
    return;
  }

  const layer = ensureZoneLabelLayer(chartApi);

  if (!layer) {
    return;
  }

  clearZoneLabels(chartApi);

  if (!zones.length) {
    return;
  }

  const chartWidth = chartApi.container.clientWidth;
  const chartHeight = chartApi.container.clientHeight;

  zones.forEach((zone) => {
    const palette = getZonePalette(zone);
    const startX = chartApi.chart.timeScale().timeToCoordinate(zone.startTime);
    const endX = chartApi.chart
      .timeScale()
      .timeToCoordinate(getZoneEndTime(zone, timeframe, lastCandleTime));
    const centerY = chartApi.candleSeries.priceToCoordinate(
      (zone.high + zone.low) / 2
    );

    if (startX == null || endX == null || centerY == null) {
      return;
    }

    const rawLeft = Math.min(startX, endX);
    const rawRight = Math.max(startX, endX);
    const visibleLeft = Math.max(18, rawLeft);
    const visibleRight = Math.min(chartWidth - 18, rawRight);

    if (visibleRight <= visibleLeft || centerY < 12 || centerY > chartHeight - 12) {
      return;
    }

    const labelX = (visibleLeft + visibleRight) / 2;
    const labelY = Math.max(18, Math.min(chartHeight - 18, centerY));
    const zonePixelWidth = Math.max(64, visibleRight - visibleLeft - 10);

    layer.appendChild(
      createZoneLabelElement(zone, palette, labelX, labelY, zonePixelWidth)
    );
  });
}

export function syncOverlayLabels(
  chartApi,
  structure,
  candles = [],
  zones = [],
  timeframe,
  lastCandleTime = null
) {
  syncBosHighlights(chartApi, structure, candles);
  syncZoneLabels(chartApi, zones, timeframe, lastCandleTime);
}
